/**
 * Shared tag-filter normalization for all inbox list surfaces.
 *
 * Tags are persisted as free-form strings by integrations, so the API matches
 * them case-insensitively. Keeping the query key built from this canonical
 * representation prevents duplicate requests when an agent selects the same
 * tags in a different order or casing.
 */
export type TagMatchMode = 'any' | 'all';

export interface TagFilterValues {
  conversationTags: string[];
  contactTags: string[];
  tagMatch: TagMatchMode;
}

export function normalizeTagNames(values?: readonly string[]): string[] {
  if (!values?.length) return [];
  return Array.from(
    new Set(
      values
        .map((value) => value.trim().toLowerCase())
        .filter(Boolean),
    ),
  ).sort();
}

export function normalizeTagFilters(input: {
  conversationTags?: readonly string[];
  contactTags?: readonly string[];
  /** Legacy single-tag alias kept for deep links and older callers. */
  tag?: string;
  tagMatch?: TagMatchMode;
}): TagFilterValues {
  const conversationTags = normalizeTagNames(
    input.conversationTags?.length ? input.conversationTags : input.tag ? [input.tag] : [],
  );
  const contactTags = normalizeTagNames(input.contactTags);
  const hasTags = conversationTags.length > 0 || contactTags.length > 0;

  return {
    conversationTags,
    contactTags,
    // `any` is the historical behavior and the API default. Avoid putting an
    // inactive `all` mode in query keys, which would create a needless cache
    // split when the user clears the last tag.
    tagMatch: hasTags && input.tagMatch === 'all' ? 'all' : 'any',
  };
}

